import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { LogOut, Home, Package, ShoppingCart, BarChart3, Menu, X } from 'lucide-react';

const AdminLayout = () => {
  const { isAuthenticated, logout, admin } = useAdminAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Səhifə dəyişəndə menyu bağlansın
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Menyu açıq olanda arxa fon sürüşməsin
  useEffect(() => {
    if (sidebarOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [sidebarOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: Home },
    { path: '/admin/products', label: 'Məhsullar', icon: Package },
    { path: '/admin/orders', label: 'Sifarişlər', icon: ShoppingCart },
    { path: '/admin/analytics', label: 'Analitika', icon: BarChart3 },
  ];

  const NavLinks = () => (
    <nav className="p-4 space-y-1">
      {menuItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-200 font-medium text-sm ${
              isActive
                ? 'bg-black text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-50 active:bg-gray-100 hover:text-black'
            }`}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Üst panel */}
      <nav className="bg-black text-white px-3 sm:px-6 py-3 sm:py-4 shadow-md sticky top-0 z-50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            {/* Burger — yalnız mobildə */}
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              aria-label="Menyu"
              className="lg:hidden flex items-center justify-center w-10 h-10 -ml-1 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors flex-shrink-0"
            >
              <Menu className="w-6 h-6" />
            </button>

            <img
              src="/laliel-logo.png"
              alt="L'Aliel"
              className="h-7 sm:h-8 flex-shrink-0"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <span className="text-base sm:text-xl font-serif tracking-widest truncate">
              L'Aliel Admin
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {/* İstifadəçi adı yalnız geniş ekranda */}
            <span className="hidden md:inline text-sm bg-gray-900 px-3 py-1.5 rounded-full border border-gray-800">
              Xoş gəldiniz, <strong>{admin?.username}</strong>
            </span>

            <button
              type="button"
              onClick={logout}
              aria-label="Çıxış"
              className="flex items-center justify-center sm:justify-start gap-2 w-10 h-10 sm:w-auto sm:h-auto sm:px-4 sm:py-2 bg-red-950/40 hover:bg-red-900/60 active:bg-red-900/80 text-red-200 border border-red-900/50 rounded-lg transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Çıxış</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="flex flex-1 min-w-0">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-64 bg-white shadow-lg sticky top-[72px] h-[calc(100vh-72px)] flex-shrink-0">
          <NavLinks />
        </aside>

        {/* Mobil: arxa fon */}
        <div
          onClick={() => setSidebarOpen(false)}
          className={`lg:hidden fixed inset-0 bg-black z-[60] transition-opacity duration-300 ${
            sidebarOpen ? 'opacity-50 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          aria-hidden="true"
        />

        {/* Mobil: sürüşən menyu */}
        <aside
          className={`lg:hidden fixed top-0 left-0 bottom-0 w-[80%] max-w-xs bg-white z-[70] shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 flex-shrink-0">
            <span className="font-serif text-lg tracking-wide">Menyu</span>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              aria-label="Bağla"
              className="flex items-center justify-center w-10 h-10 -mr-1 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain">
            <NavLinks />
          </div>

          <div className="flex-shrink-0 border-t border-gray-100 px-4 py-4">
            <p className="text-xs text-gray-500 mb-1">Daxil olub:</p>
            <p className="text-sm font-semibold text-gray-900">{admin?.username}</p>
          </div>
        </aside>

        {/* Əsas məzmun */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
